import {Injectable} from '@angular/core';
import {DoorCorners, Orientation, ScenarioImage, ScenarioType} from '../../models/scenario-image';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter, interval,
  lastValueFrom,
  mergeMap,
  Observable,
  of,
  ReplaySubject, Subject,
  Subscription, switchMap,
  take, takeUntil,
  zip
} from 'rxjs';
import {ScenarioImageService} from './scenario-image.service';
import {ScenarioImageLoader, UploadScenarioArgs} from './scenario.image.loader';
import {DoorService} from '../door/door.service';
import { DoorType} from '../../models/door';
import {CustomerServiceImpl} from "../customer/customer.service.impl";
import {DeviceType, GetDefaultCornersGQL} from "../../../generated/graphql";
import {CustomerService} from "../customer/customer.service";
import {Size} from "../../models/utils";
import {mapSmallGraphQLProductTypeToSmallProductType} from "../product-type/product-type";

@Injectable({providedIn: 'root'})
export class ScenarioImageServiceImpl extends ScenarioImageService {

  scenarios$ = new BehaviorSubject<ScenarioImage[]>([]);
  selected$ = new BehaviorSubject<ScenarioImage | undefined>(undefined);
  orientationFilter$ = new ReplaySubject<Orientation>(1);

  scenarioStorageKey = 'USER_SCENARIOS';

  exampleImageSet$ = new BehaviorSubject<number | undefined>(undefined);

  // infinite subscription to change loaded scenarios depending on other stuff
  scenarioLoadingSub?: Subscription;

  constructor(private scenarioImageLoader: ScenarioImageLoader,
              private getDefaultCornersGQL: GetDefaultCornersGQL,
              private customerService: CustomerService,
              private doorService: DoorService) {
    super();

    this.getSelected$().subscribe(scenarioImage => {
      const currentIndex = this.doorService.getSelectedDoorCornersIndexValue()

      // Check if the new scenario has enough corners
      if (scenarioImage && scenarioImage.corners.length < currentIndex) {
        this.doorService.selectDoorCornersIndex(1)
      }
    })
  }

  public reloadScenarios(orientation: Orientation, currentDoorTypeId: string): void {
    this.isLoading$.next(true);
    zip([
      this.scenarioImageLoader.loaderUserScenarioForAppUser(),
      this.scenarioImageLoader.loadExampleImages({
        customerKey: CustomerServiceImpl.GetCustomerKey(),
        doorTypeId: currentDoorTypeId, orientation,
        setNumber: this.exampleImageSet$.getValue()
      })
    ]).pipe(take(1)).subscribe({
      next: result => {
        const merged = [...result[0], ...result[1]];
        this.scenarios$.next(merged);

        // if example scenario's door type is different from currently selected door, change example scenario to match door type

        // Since now we can have multiple doorCorners in a single examle image we need to adjust this auto switch
        // We will only switch if the current scenario is an example scenario with only one doorCorner

        // TODO this whole thing just feels weird. Do we really still need this for some special customer use case?
        /*const currentScenario = this.selected$.getValue();
        if (currentScenario) {
          if (currentScenario.type === ScenarioType.EXAMPLE
            && currentScenario.corners.length === 1
            && currentScenario.corners[0].doorType !== currentDoor.doorType) {
            const nextExampleScenario = this.scenarios$.getValue().find(s => s.type === ScenarioType.EXAMPLE && s.doorType === currentDoor.doorType);
            if (nextExampleScenario) {
              this.selected$.next(nextExampleScenario);
            }
          }
        }*/

        this.isLoading$.next(false);
      },
      error: err => {
        console.warn('could not load scenarios', err);
        this.isLoading$.next(false);
        this.scenarioLoadingSub?.unsubscribe();
        this.scenarioLoadingSub = undefined;
      }
    });
  }

  public getScenariosObservable(): Observable<Array<ScenarioImage>> {
    return this.scenarios$.asObservable();
  }

  /**
   * loads all examples images or returns already loaded ones
   */
  public getScenarios$(): Observable<Array<ScenarioImage>> {
    const isFloorEnvironment = CustomerServiceImpl.GetCustomerKey() === 'floors';

    // only load example images from backend once
    if (!this.scenarioLoadingSub) {

      // For the floor environment, we don't want to reload all the scenarios based on the current door type.
      if (isFloorEnvironment) {
        this.scenarioLoadingSub = combineLatest([
          this.orientationFilter$.pipe(distinctUntilChanged()),
        ]).subscribe({
          next: ([orientation]) => {
            // @ts-expect-error old ts-ignore
            this.reloadScenarios(orientation, {doorType: DoorType.SINGLE});
          },
          complete: () => {
            console.error('this subscription should never complete');
          }
        });
      } else {
        this.scenarioLoadingSub = combineLatest([
          this.orientationFilter$.pipe(distinctUntilChanged()),
          this.doorService.getSelectedDoorType$().pipe(distinctUntilChanged((prev, curr) => prev === curr))
        ]).subscribe({
          next: ([orientation, currentDoorType]) => {
            if (!currentDoorType) {
              return
            }
            this.reloadScenarios(orientation, currentDoorType.id);
          },
          complete: () => {
            console.error('this subscription should never complete');
          }
        });
      }
    }

    return this.scenarios$.asObservable();
  }

  public getSelected$(): Observable<ScenarioImage> {
    return this.selected$.asObservable().pipe(mergeMap(s => {
      if (!s) {
        return EMPTY;
      }
      return of(s);
    }));
  }

  public getSelected(): ScenarioImage | undefined {
    return this.selected$.getValue();
  }

  public selectScenario(image: ScenarioImage): void {
    this.selected$.next(image);
  }

  public async selectScenarioByShortId(shortId: string, type: string): Promise<boolean> {
    if (this.selected$.getValue()?.shortId === shortId) {
      return true;
    }

    if (type === ScenarioType.USER) {
      const scenario = await lastValueFrom(this.scenarioImageLoader.loadUserScenarioImageByShortId(shortId).pipe(take(1)));
      this.selectScenario(scenario);
      return true
    } else if (type === ScenarioType.EXAMPLE) {
      const scenarios = await lastValueFrom(this.getScenarios$().pipe(
        filter(scenarios => scenarios.length !== 0),
        take(1)
      ));
      const toSelect = scenarios.find(s => s.type === type && s.shortId === shortId);
      if (toSelect) {
        this.selected$.next(toSelect);
        return true
      } else {
        console.warn('example image not found in image gallery');
        return false
      }
    } else {
      console.warn('unknown scenario type', type);
      return false
    }
  }

  public async updateCorners(corners: Array<DoorCorners>): Promise<void> {
    const currentScenario = this.selected$.getValue();
    if (currentScenario) {

      // Has clipped corners
      const hasClippedCorners = corners.some(c => c.hasClippedCorners)

      const updatedScenario = await lastValueFrom(this.scenarioImageLoader.updateCorners(currentScenario.id, corners,
        hasClippedCorners ? {clippedDoorCorners: corners} : undefined));

      const images = this.scenarios$.getValue();
      const index = images.findIndex(s => s.id === updatedScenario.id);
      images[index] = updatedScenario;

      // update list and current scenario
      this.scenarios$.next(images);
      this.selected$.next(updatedScenario);

      this.startPollingForVisualisationImprovement(updatedScenario)
    }
    // TODO: error handling if selected not set
  }

  public async addDoorToCurrentScenario({imageSize}: { imageSize: Size }): Promise<{ doorCorners: DoorCorners, index: number }> {
    const currentScenario = this.selected$.getValue();
    if (!currentScenario) {
      throw new Error('Could not get current scenario')
    }

    const customerConfig = this.customerService.getCustomerConfig()
    if (!customerConfig) {
      throw new Error('Could not get customer config')
    }

    const getSelectedDoorAtCurrentIndex = this.doorService.getSelectedDoorAtCurrentIndex()
    const doorTypeId = getSelectedDoorAtCurrentIndex?.doorType.id || customerConfig.defaultDoorType.id

    // const doorLocationLimits = this.calculateDoorPositionLimits(currentScenario.corners, imageSize)

    const newDoorCorners = await this.getNewDefaultCornersForCurrentScenario({
      doorTypeId,
      imageSize,
      // doorLocation: doorLocationLimits,
    })

    let firstFreeIndex = 1
    while (currentScenario.corners.find(c => c.index === firstFreeIndex)) {
      firstFreeIndex++
    }
    newDoorCorners.index = firstFreeIndex

    currentScenario?.corners.push(newDoorCorners)

    this.selected$.next(currentScenario);
    return {
      index: firstFreeIndex,
      doorCorners: newDoorCorners
    }
  }

  private calculateDoorPositionLimits(doorCorners: Array<DoorCorners>, imageSize: Size): { start: number, end: number } {
    const doorCornersWithMaxWidth = doorCorners.map(c => {
      const minX = Math.min(
        c.topLeft.x,
        c.topRight.x,
        c.bottomRight.x,
        c.bottomLeft.x
      );
      const maxX = Math.max(
        c.topLeft.x,
        c.topRight.x,
        c.bottomRight.x,
        c.bottomLeft.x
      );
      const maxWidth = maxX - minX;
      return {
        ...c,
        maxWidth
      }
    })

    const doorCornersWithMaxWidthSortedByMinX = doorCornersWithMaxWidth.sort((a, b) => {
      const aMinX = Math.min(
        a.topLeft.x,
        a.topRight.x,
        a.bottomRight.x,
        a.bottomLeft.x
      );

      const bMinX = Math.min(
        b.topLeft.x,
        b.topRight.x,
        b.bottomRight.x,
        b.bottomLeft.x
      );

      return aMinX - bMinX
    })

    // Find the door location for the new door
    // It should be placed where the most space is in the image with the already given door Corners
    let biggestSpace = {
      start: 0,
      end: imageSize.width,
      space: 0,
    }

    for (let i = 0; i < doorCornersWithMaxWidthSortedByMinX.length + 1; i++) {

      let previousMaxX = 0
      if (i === 0) {
        previousMaxX = 0
      } else {
        const previousDoor = doorCornersWithMaxWidthSortedByMinX[i - 1]
        previousMaxX = Math.max(
          previousDoor.topLeft.x,
          previousDoor.topRight.x,
          previousDoor.bottomRight.x,
          previousDoor.bottomLeft.x
        );
      }

      let nextMinX = imageSize.width
      if (i === doorCornersWithMaxWidthSortedByMinX.length) {
        nextMinX = imageSize.width
      } else {
        const nextDoor = doorCornersWithMaxWidthSortedByMinX[i]
        nextMinX = Math.min(
          nextDoor.topLeft.x,
          nextDoor.topRight.x,
          nextDoor.bottomRight.x,
          nextDoor.bottomLeft.x
        );
      }

      const space = nextMinX - previousMaxX

      if (space > biggestSpace.space) {
        biggestSpace = {
          start: previousMaxX,
          end: nextMinX,
          space
        }
      }
    }
    return biggestSpace
  }

  public async uploadUserScenario({file, deviceType}: { file: File, deviceType: DeviceType }): Promise<{
    showRefineLayout: boolean
  }> {
    try {

      const currentDoor = this.doorService.getSelectedDoorAtCurrentIndex()

      const args: UploadScenarioArgs = {
        customerKey: CustomerServiceImpl.GetCustomerKey(),
        file,
        deviceType,
        doorTypeId: currentDoor?.doorType.id,
      }

      const {scenarioImage, showRefineLayout} = await lastValueFrom(this.scenarioImageLoader.uploadUserScenario(args))

      this.scenarios$.next([scenarioImage, ...this.scenarios$.getValue()]);
      this.selected$.next(scenarioImage);

      this.startPollingForVisualisationImprovement(scenarioImage)

      return {showRefineLayout};
    } catch (e) {
      console.warn('could not upload scenario', e);
      return {showRefineLayout: false};
    }
  }

  private stopPolling$ = new Subject<void>();

  private startPollingForVisualisationImprovement(scenarioImage: ScenarioImage): void {
    this.stopPolling$ = new Subject<void>();
    if (!scenarioImage.isWaitingForVisualizationImprovement) {
      return
    }

    const polling$ = interval(1000).pipe(
      switchMap(() =>
        this.scenarioImageLoader.loadUserScenarioImageByShortId(scenarioImage.shortId).pipe(take(1))
      ),
      takeUntil(this.stopPolling$)
    );

    polling$.subscribe({
      next: async (updatedScenario) => {
        if (!updatedScenario.isWaitingForVisualizationImprovement) {
          console.info(' - scenario is ready');
          this.stopPolling$.next(); // Emit signal to stop polling

          this.selected$.next(updatedScenario);

          // Update the scenario in the list
          const currentScenarios = this.scenarios$.getValue()
          const index = currentScenarios.findIndex(s => s.id === updatedScenario.id);
          currentScenarios[index] = updatedScenario;
          this.scenarios$.next(currentScenarios);
        } else {
          console.info(' - scenario is still not ready');
        }
      },
      error: (err) => {
        console.error('Polling error:', err);
        this.stopPolling$.next(); // Stop polling on error
      },
    });
  }

  public stopPollingForVisualisationImprovements(): void {
    this.stopPolling$.next()
    this.stopPolling$.complete()
  }

  public deleteScenario(userScenarioShortId: string): void {
    this.scenarioImageLoader.deleteUserScenarioForAppUser(userScenarioShortId).subscribe(() => {
      if (userScenarioShortId === this.getSelected()?.shortId) {
        // select next scenario
        const nextScenario = this.scenarios$.getValue().find(s => s.shortId !== userScenarioShortId);
        this.selected$.next(nextScenario);
      }
      // reload all scenarios
      combineLatest([
        this.orientationFilter$.pipe(take(1)),
        this.doorService.getSelectedDoorType$().pipe(take(1))
      ]).subscribe({
        next: ([orientation, selectedDoorType]) => {
          if (!selectedDoorType) {
            return
          }
          this.reloadScenarios(orientation, selectedDoorType.id);
        }
      });
    });
  }

  public setOrientationFilter(orientation: Orientation): void {
    this.orientationFilter$.next(orientation);
  }

  public getCurrentOrientationValue(): Promise<Orientation | undefined> {
    return lastValueFrom(this.orientationFilter$.pipe(take(1)));
  }

  public setExampleImageSetNumber(setNumber: number): void {
    this.exampleImageSet$.next(setNumber);
  }

  public async getNewDefaultCornersForCurrentScenario({
                                                        doorTypeId,
                                                        imageSize,
                                                      }: {
    doorTypeId: string,
    imageSize: { width: number, height: number },
  }): Promise<DoorCorners> {

    const result = await this.getDefaultCornersGQL.fetch({
      doorTypeId,
      imageHeight: imageSize.height,
      imageWidth: imageSize.width,
    }, {fetchPolicy: 'no-cache'}).toPromise()

    if (!result || result.errors || !result.data) {
      throw new Error('Could not get default corners')
    }

    return {
      ...result.data.getDefaultCorners,
      doorType: mapSmallGraphQLProductTypeToSmallProductType(result.data.getDefaultCorners.doorType)
    }
  }
}
